import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IconClose } from '../icons';

export interface PickerModalProps<T extends number = number> {
  open: boolean;
  title: string;
  value: T;
  options: T[];
  onChoose: (value: T) => void;
  onClose: () => void;
  format?: (o: T) => string;
  includeOff?: boolean;
  offLabel?: string;
  confirmLabel?: string | null;
  /**
   * Max height of the options grid before it starts scrolling. When the
   * game publishes a large bet ladder (e.g. 30+ steps), the grid spills
   * off the bottom of the sheet — capping it here turns the overflow
   * into a scroll area instead. Accepts any valid CSS length or `calc`
   * expression. Default: `'44vh'` (≈ 6 rows in the default layout).
   */
  maxGridHeight?: number | string;
}

/**
 * Shared bottom-sheet picker used by bet and autospin modals.
 * When `confirmLabel` is set, options stage a selection and a primary button confirms.
 */
export function PickerModal<T extends number = number>({
  open,
  title,
  value,
  options,
  onChoose,
  onClose,
  format = (o) => String(o),
  includeOff = false,
  offLabel = 'Off',
  confirmLabel = null,
  maxGridHeight = '44vh',
}: PickerModalProps<T>) {
  const [staged, setStaged] = useState<T>(value);
  useEffect(() => {
    if (open) setStaged(value);
  }, [open, value]);

  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => setVisible(true)),
      );
    } else if (mounted) {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 280);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, mounted]);

  // When the sheet opens with a long option list, the currently-selected
  // option might be scrolled off-screen inside the grid. Center it so the
  // player can see where they are before choosing something else.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef<HTMLButtonElement | null>(null);
  useLayoutEffect(() => {
    if (!visible) return;
    const scroller = scrollRef.current;
    const active = activeRef.current;
    if (!scroller || !active) return;
    // Manual centering (instead of scrollIntoView) so we don't cause the
    // whole page / device frame to scroll alongside the inner grid —
    // scrollIntoView bubbles up through scrollable ancestors.
    const top = active.offsetTop - scroller.clientHeight / 2 + active.clientHeight / 2;
    scroller.scrollTop = Math.max(0, top);
  }, [visible]);

  if (!mounted) return null;

  const opts: T[] = includeOff ? ([0, ...options] as T[]) : options;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          // Solid scrim instead of blur. iOS Safari has to composite the
          // entire layer behind a `backdrop-filter` — with a live cross-
          // origin reels iframe sitting under us, that path is the single
          // most reliable way to crash the tab on older devices. A slightly
          // darker scrim (0.7 vs 0.55) reads almost identical to the eye.
          background: 'rgba(0,0,0,0.7)',
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.25s ease',
        }}
      />
      <div
        style={{
          position: 'relative',
          width: '100%',
          background: 'var(--surface)',
          borderTop: '1px solid var(--border-2)',
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          padding: '18px 20px 24px',
          boxShadow: 'var(--shadow)',
          transform: visible ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.32s cubic-bezier(0.37, 0, 0.63, 1)',
        }}
      >
        <div
          style={{
            width: 44,
            height: 4,
            borderRadius: 2,
            background: 'var(--border-2)',
            margin: '0 auto 14px',
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 14,
          }}
        >
          <div
            style={{
              fontSize: 13.2,
              fontWeight: 400,
              letterSpacing: 2,
              textTransform: 'uppercase',
              fontFamily: 'Oswald, sans-serif',
              color: 'var(--accent)',
            }}
          >
            {title}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <IconClose size={16} />
          </button>
        </div>
        <div
          ref={scrollRef}
          className="reelay-picker-scroll"
          style={{
            maxHeight:
              typeof maxGridHeight === 'number'
                ? `${maxGridHeight}px`
                : maxGridHeight,
            overflowY: 'auto',
            // Pad the right edge so the scrollbar (when present) doesn't
            // crowd the last column of buttons. Negative margin cancels
            // the padding so the grid still hugs the sheet edges when
            // there's no overflow.
            paddingRight: 6,
            marginRight: -6,
            // Small vertical breathing room — keeps the top/bottom rows
            // from butting against the header / confirm button and
            // signals the scroll container visually.
            paddingTop: 2,
            paddingBottom: 2,
            // Firefox + modern browsers: thin themed scrollbar without
            // needing a global stylesheet. WebKit styling lives in the
            // className rule injected below.
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--border-2) transparent',
            WebkitOverflowScrolling: 'touch',
            // The effective touch-action for a touch is the intersection of
            // its own value and every ancestor's — since html/body now sets
            // `touch-action: none` (see demo.css) to stop iOS's rubber-band
            // bounce from fighting ReelsStack's swipe gesture, this grid
            // needs its own explicit `pan-y` or vertical scrolling here
            // would silently break too.
            touchAction: 'pan-y',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 8,
            }}
          >
            {opts.map((o) => {
              const compareTo = confirmLabel ? staged : value;
              const active = o === compareTo;
              return (
                <button
                  key={o}
                  ref={active ? activeRef : undefined}
                  onClick={() => {
                    if (confirmLabel) {
                      setStaged(o);
                    } else {
                      onChoose(o);
                      onClose();
                    }
                  }}
                  style={{
                    padding: '14px 10px',
                    background: active ? 'var(--accent)' : 'var(--surface-2)',
                    color: active ? 'var(--accent-ink)' : 'var(--text)',
                    border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 10,
                    cursor: 'pointer',
                    fontFamily: 'Oswald, sans-serif',
                    fontSize: 18,
                    fontWeight: 400,
                    transition:
                      'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
                  }}
                >
                  {o === 0 && includeOff ? offLabel : format(o)}
                </button>
              );
            })}
          </div>
        </div>
        {confirmLabel && (
          <button
            onClick={() => {
              onChoose(staged);
              onClose();
            }}
            disabled={!staged}
            style={{
              marginTop: 16,
              width: '100%',
              padding: '14px 16px',
              background: staged ? 'var(--accent)' : 'var(--surface-2)',
              color: staged ? 'var(--accent-ink)' : 'var(--text-muted)',
              border: `1px solid ${staged ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 12,
              cursor: staged ? 'pointer' : 'not-allowed',
              fontFamily: 'Oswald, sans-serif',
              fontSize: 16,
              fontWeight: 400,
              letterSpacing: 2,
              textTransform: 'uppercase',
              transition: 'background 0.15s ease, color 0.15s ease',
            }}
          >
            {confirmLabel}
          </button>
        )}
      </div>
    </div>
  );
}

export interface BetModalProps {
  open: boolean;
  value: number;
  options?: number[];
  currency?: string;
  onChoose: (value: number) => void;
  onClose: () => void;
  /** Forwarded to PickerModal — cap before the grid starts scrolling. */
  maxGridHeight?: number | string;
}

// Memoized — always mounted (SlotUI renders it unconditionally, gated by
// `open`), so without this it re-renders on every unrelated SlotUI render
// (including swipe-drag frames) even while closed and invisible. Paired
// with SlotUI passing stable onChoose/onClose references.
export const BetModal = memo(function BetModal({
  open,
  value,
  options = [50, 100, 200, 300, 500, 1000, 2000],
  currency = '$',
  onChoose,
  onClose,
  maxGridHeight,
}: BetModalProps) {
  return (
    <PickerModal
      open={open}
      title="Select bet"
      value={value}
      options={options}
      onChoose={onChoose}
      onClose={onClose}
      format={(o) => `${o}${currency}`}
      maxGridHeight={maxGridHeight}
    />
  );
});

export interface AutospinsModalProps {
  open: boolean;
  value: number;
  options?: number[];
  onChoose: (value: number) => void;
  onClose: () => void;
  /** Forwarded to PickerModal — cap before the grid starts scrolling. */
  maxGridHeight?: number | string;
}

// Memoized — same reasoning as BetModal above.
export const AutospinsModal = memo(function AutospinsModal({
  open,
  value,
  options = [10, 25, 50, 100, 250, 500],
  onChoose,
  onClose,
  maxGridHeight,
}: AutospinsModalProps) {
  return (
    <PickerModal
      open={open}
      title="Autospins"
      value={value}
      options={options}
      onChoose={onChoose}
      onClose={onClose}
      confirmLabel="Start"
      maxGridHeight={maxGridHeight}
    />
  );
});
